import React, { useEffect, useRef } from 'react';
import { Lightbulb, Loader2 } from 'lucide-react';
import { useDevice } from '@/app/lib/contexts/DeviceContext';
import { useTranslations } from 'next-intl';

interface HintPanelProps {
  isVisible: boolean;
  isLoading: boolean;
  hints: string[];
  onClose: () => void;
  onSelectHint: (hintText: string) => void;
  lightbulbButtonRef?: React.RefObject<HTMLButtonElement | null>;
}

export const HintPanel: React.FC<HintPanelProps> = ({
  isVisible,
  isLoading,
  hints,
  onClose,
  onSelectHint,
  lightbulbButtonRef,
}) => {
  const t = useTranslations('chat');
  const panelRef = useRef<HTMLDivElement>(null);
  const { isMobile, isDesktop } = useDevice();

  // Handle click outside to close
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // Don't close if clicking on the lightbulb button
      if (lightbulbButtonRef?.current && lightbulbButtonRef.current.contains(event.target as Node)) {
        return;
      }
      
      if (panelRef.current && !panelRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    if (isVisible) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isVisible, onClose, lightbulbButtonRef]);

  // Prevent body scroll when panel is open on mobile
  useEffect(() => {
    if (isVisible && isMobile) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isVisible, isMobile]);

  if (!isVisible) return null;

  // Mobile version - bottom-up panel
  if (isMobile) {
    return (
      <>
        {/* Add CSS for slide-up animation */}
        <style jsx>{`
          @keyframes slideUp {
            from {
              transform: translateY(100%);
            }
            to {
              transform: translateY(0);
            }
          }
        `}</style>
        
        {/* Mobile Hint Panel */}
        <div className="fixed inset-0 z-50 flex items-end">
          {/* Dark overlay */}
          <div 
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={onClose}
          />
          
          {/* Bottom-up panel */}
          <div
            ref={panelRef}
            className="relative w-full rounded-t-[20px] overflow-hidden"
            style={{
              height: '80vh',
              animation: 'slideUp 0.3s ease-out'
            }}
          >
            {/* Localized overlay with blurred background - same as desktop */}
            <div className="absolute inset-0 bg-black/40 backdrop-blur-sm rounded-t-[20px]" />
            
            {/* Handle bar */}
            <div className="relative flex justify-center mb-6 pt-4">
              <div className="w-12 h-1 bg-gray-600 rounded-full"></div>
            </div>
            
            {/* Panel content - completely scrollable */}
            <div className="relative px-6 pb-12 h-full overflow-y-auto">
              {/* Loading state */}
              {isLoading && (
                <div className="flex items-center gap-3 text-slate-300">
                  <div className="relative">
                    <Loader2 className="w-4 h-4 sm:w-5 sm:h-5 animate-spin text-blue-400" />
                    <div className="absolute inset-0 w-4 h-4 sm:w-5 sm:h-5 bg-blue-400/20 rounded-full animate-ping"></div>
                  </div>
                  <span className="text-sm sm:text-base font-medium">{t('loadingHints')}</span>
                </div>
              )}

              {/* Hints content */}
              {!isLoading && (
                <>
                  {hints.length > 0 ? (
                    <div className="space-y-2 sm:space-y-3">
                      {hints.map((hint, index) => (
                        <button
                          key={index}
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            onSelectHint(hint);
                            onClose();
                          }}
                          className="group w-full text-left p-3 sm:p-4 bg-purple-900/10 hover:bg-purple-800/30 rounded-xl sm:rounded-2xl transition-all duration-300 border border-purple-200/30 hover:border-purple-100/80 hover:shadow-lg hover:shadow-purple-300/20 backdrop-blur-2xl active:scale-95"
                        >
                          <span className="text-white text-sm sm:text-base leading-normal font-medium group-hover:text-purple-50 transition-colors duration-300">
                            {hint}
                          </span>
                        </button>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-4 sm:py-6">
                      <div className="w-8 h-8 sm:w-12 sm:h-12 mx-auto mb-2 sm:mb-3 bg-slate-700/50 rounded-full flex items-center justify-center">
                        <Lightbulb className="w-4 h-4 sm:w-6 sm:h-6 text-slate-400" />
                      </div>
                      <span className="text-slate-400 text-sm sm:text-base font-medium">{t('noHintsAvailable')}</span>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </>
    );
  }

  // Desktop version - keep as is
  // If loading, show the compact loading panel
  if (isLoading) {
    return (
      <div 
        ref={panelRef}
        className="absolute bottom-full left-0 right-0 mb-1 backdrop-blur-xl border rounded-2xl shadow-2xl z-50 overflow-hidden bg-gradient-to-br from-slate-900/95 via-slate-800/95 to-slate-900/95 border-slate-600/30"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
      >
        {/* Glow effect - only show during loading */}
        <div className="absolute inset-0 bg-gradient-to-r from-purple-300/15 via-pink-300/15 to-purple-200/15 rounded-2xl"></div>
        
        {/* Content */}
        <div className="relative p-3 sm:p-4">
          <div className="flex items-center gap-3 text-slate-300">
            <div className="relative">
              <Loader2 className="w-4 h-4 sm:w-5 sm:h-5 animate-spin text-blue-400" />
              <div className="absolute inset-0 w-4 h-4 sm:w-5 sm:h-5 bg-blue-400/20 rounded-full animate-ping"></div>
            </div>
            <span className="text-xs sm:text-sm font-medium">{t('loadingHints')}</span>
          </div>
        </div>
      </div>
    );
  }

  // If hints are loaded, show full overlay with blurred background
  return (
    <div 
      ref={panelRef}
      className="absolute bottom-full left-0 right-0 mb-1 z-50 overflow-hidden"
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}
    >
      {/* Localized overlay with blurred background - contained within the panel wrapper */}
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm rounded-2xl" />
      
      {/* Hint panel with responsive styling */}
      <div className="relative p-3 sm:p-4">
        {hints.length > 0 ? (
          <div className="space-y-2 sm:space-y-3">
            {hints.map((hint, index) => (
              <button
                key={index}
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onSelectHint(hint);
                  onClose();
                }}
                className="group w-full text-left p-3 sm:p-4 bg-purple-900/10 hover:bg-purple-800/30 rounded-xl sm:rounded-2xl transition-all duration-300 border border-purple-200/30 hover:border-purple-100/80 hover:shadow-lg hover:shadow-purple-300/20 backdrop-blur-2xl active:scale-95"
              >
                <span className="text-white text-xs sm:text-sm leading-relaxed font-medium group-hover:text-purple-50 transition-colors duration-300">
                  {hint}
                </span>
              </button>
            ))}
          </div>
        ) : (
          <div className="text-center py-4 sm:py-6">
            <div className="w-8 h-8 sm:w-12 sm:h-12 mx-auto mb-2 sm:mb-3 bg-slate-700/50 rounded-full flex items-center justify-center">
              <Lightbulb className="w-4 h-4 sm:w-6 sm:h-6 text-slate-400" />
            </div>
            <span className="text-slate-400 text-xs sm:text-sm font-medium">{t('noHintsAvailable')}</span>
          </div>
        )}
      </div>
    </div>
  );
};
